import React, { useState, useEffect, useRef } from "react";
import MobileReservationModal from "../components/MobileReservationModal";
import MobileSearchModal from "../components/MobileSearchModal";
import FloorplanOverlay from "../components/FloorplanOverlay";
import Calendar from "react-calendar";
import MobileEditModal from "../components/MobileEditModal";
import FloorplanPrintModal from "../components/FloorplanPrintModal";
import SettingsModal from "../components/SettingsModal";

const MobileDashboard = ({ user, onLogout }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewReservation, setShowNewReservation] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showFloorplan, setShowFloorplan] = useState(false);
  const [showFloorplanPrint, setShowFloorplanPrint] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const todayStr = new Date().toLocaleDateString("en-CA");
  const selectedStr = selectedDate.toLocaleDateString("en-CA");
  const isToday = selectedStr === todayStr;

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;

    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;

    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // Minimalna udaljenost za swipe
    const MIN_SWIPE = 60;

    // Ignoruj ako je modal otvoren
    if (showNewReservation || showSearch || showFloorplan ||
        showFloorplanPrint || showSettings || showDatePicker) {
      touchStartX.current = null;
      return;
    }

    if (absDeltaX > absDeltaY && absDeltaX > MIN_SWIPE) {
      // Horizontalni swipe
      if (deltaX > 0) {
        // Swipe lijevo → otvori Prikaz stolova
        setShowFloorplan(true);
      }
      // Swipe desno – nema akcije na glavnom dashboardu
    }

    touchStartX.current = null;
    touchStartY.current = null;
  };

  const dateLabel = isToday
    ? "Danas"
    : selectedDate.toLocaleDateString("hr-HR", { day: "numeric", month: "long", year: "numeric" });

  const [stats, setStats] = useState({
    total_reservations: 0,
    total_guests: 0,
    free_tables: {
      morning_indoor: 0, morning_outdoor: 0,
      afternoon_indoor: 0, afternoon_outdoor: 0,
      evening_indoor: 0, evening_outdoor: 0,
    }
  });

  const [selectedReservation, setSelectedReservation] = useState(null);
  const [confirmDeleteMobile, setConfirmDeleteMobile] = useState(null);

  useEffect(() => {
    fetch("/reservations/api/shifts/")
      .then(res => res.json())
      .then(data => setShifts(data.shifts || []));

    fetch("/reservations/api/tables/")
      .then(res => res.json())
      .then(data => setTables(data.tables || []));
  }, []);

	useEffect(() => {
		fetchReservations();

		fetch(`/reservations/api/reservations/stats/?date=${selectedStr}`)
			.then(res => res.json())
			.then(data => setStats(data))
			.catch(() => {});
	}, [selectedDate]);

  useEffect(() => {
    fetchReservations();
  }, [selectedDate]);

  const fetchReservations = () => {
    setLoading(true);
    fetch(`/reservations/api/reservations/by-date/?date=${selectedStr}`)
      .then(res => res.json())
      .then(data => {
        const sorted = (data.reservations || []).sort((a, b) => {
          if (a.start_datetime && b.start_datetime)
            return a.start_datetime.localeCompare(b.start_datetime);
          return 0;
        });
        setReservations(sorted);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const refreshData = () => {
    fetchReservations();
    fetch(`/reservations/api/reservations/stats/?date=${selectedStr}`)
      .then(res => res.json())
      .then(data => setStats(data))
      .catch(() => {});
  };

  const getPeriodColor = (time) => {
    if (!time) return "bg-gray-700";
    const [h] = time.split(":").map(Number);
    if (h >= 8 && h < 12) return "bg-blue-800";
    if (h >= 12 && h < 20) return "bg-yellow-700";
    return "bg-red-900";
  };

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const isPast = (r) => {
    if (!isToday || !r.start_datetime) return false;
    const [h, m] = r.start_datetime.split(":").map(Number);
    return (h * 60 + m) < nowMinutes;
  };

  const upcoming = reservations.filter(r => !isPast(r));
  const past = reservations.filter(r => isPast(r));
  const allSorted = [...upcoming, ...past];

  return (
    <div 
      className="min-h-screen bg-black text-white flex flex-col"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      >

      {/* Header */}
      <div className="border-b border-[#c1b428]">

        <div
          className="bg-black flex items-center justify-center py-1 px-4"
          style={{ height: "42px" }}
        >
          <img
            src="/pub_cover.jpg"
            alt="The House Pub"
            style={{
              maxHeight: "38px",
              maxWidth: "100%",
              objectFit: "contain"
            }}
          />
        </div>

        <div className="bg-gray-900 px-4 py-1 flex justify-between items-center">

          {/* Lijeva strana */}
          <div className="flex items-center gap-3">
            <span className="text-[#c1b428] text-xs">
              👤 {user?.username}
              <span className="text-gray-400"> ({user?.role})</span>
            </span>

            <button
              onClick={onLogout}
              className="text-red-400 text-xs hover:text-red-300"
            >
              Odjava
            </button>
          </div>

          {/* Desna strana */}
          <button
            onClick={() => setShowSettings(true)}
            className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-800 border border-gray-700 text-[#c1b428] hover:bg-gray-700 transition"
          >
            ⚙️
          </button>

        </div>
      </div>

      {/* Datum picker */}
      <div className="px-4 py-3 bg-gray-900 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center gap-2 text-[#c1b428] font-bold text-lg"
          >
            📅 {dateLabel}
            <span className="text-xs text-gray-400">{showDatePicker ? "▲" : "▼"}</span>
          </button>
          {!isToday && (
            <button
              onClick={() => { setSelectedDate(new Date()); setShowDatePicker(false); }}
              className="text-xs bg-gray-700 text-[#c1b428] px-2 py-1 rounded hover:bg-gray-600"
            >
              Danas
            </button>
          )}
        </div>

        {showDatePicker && (
          <div className="mt-2">
            <Calendar
              onChange={(date) => {
                setSelectedDate(date);
                setShowDatePicker(false); // ✅ automatski zatvori
              }}
              value={selectedDate}
              className="react-calendar text-[#c1b428] bg-gray-800 border-none w-full"
              locale="hr-HR"
            />
          </div>
        )}

      </div>

			{/* Statistika */}
			<div className="px-4 py-2 bg-gray-900 border-b border-gray-700">
				<div className="grid grid-cols-2 gap-2 text-xs mb-2">
					<div className="bg-gray-800 rounded-lg p-2 text-center">
						<div className="text-[#c1b428] font-bold text-lg">{stats.total_reservations}</div>
						<div className="text-gray-400">Rezervacija</div>
					</div>
					<div className="bg-gray-800 rounded-lg p-2 text-center">
						<div className="text-[#c1b428] font-bold text-lg">{stats.total_guests}</div>
						<div className="text-gray-400">Gostiju</div>
					</div>
				</div>

				<div className="bg-gray-800 rounded-lg p-2 text-xs">
					<div className="flex justify-between items-center mb-1">
						<span className="text-gray-400">Slobodnih stolova:</span>
						<span className="flex gap-3 text-[#c1b428] font-semibold">
							<span>🏠</span><span>🌿</span>
						</span>
					</div>
					<div className="flex justify-between items-center">
						<span className="text-gray-400">☀️ Jutro:</span>
						<span className="flex gap-3 font-semibold">
							<span className="w-5 text-center">{stats.free_tables.morning_indoor}</span>
							<span className="w-5 text-center">{stats.free_tables.morning_outdoor}</span>
						</span>
					</div>
					<div className="flex justify-between items-center">
						<span className="text-gray-400">🌤️ Poslijepodne:</span>
						<span className="flex gap-3 font-semibold">
							<span className="w-5 text-center">{stats.free_tables.afternoon_indoor}</span>
							<span className="w-5 text-center">{stats.free_tables.afternoon_outdoor}</span>
						</span>
					</div>
					<div className="flex justify-between items-center">
						<span className="text-gray-400">🌙 Večer:</span>
						<span className="flex gap-3 font-semibold">
							<span className="w-5 text-center">{stats.free_tables.evening_indoor}</span>
							<span className="w-5 text-center">{stats.free_tables.evening_outdoor}</span>
						</span>
					</div>
				</div>
			</div>

      {/* Akcijski gumbi */}
      <div className="px-4 py-2 grid grid-cols-2 gap-2">
        {["moderator", "admin"].includes(user?.role) && (
          <button
            onClick={() => setShowNewReservation(true)}
            className="col-span-2 flex items-center justify-center gap-2 bg-[#c1b428] text-black font-bold rounded-xl py-3 text-sm"
          >
            ➕ Nova rezervacija
          </button>
        )}
        <button
          onClick={() => setShowSearch(true)}
          className="flex items-center justify-center gap-2 bg-gray-800 text-[#c1b428] border border-gray-700 rounded-xl py-3 text-xs font-semibold"
        >
          🔍 Pretraga
        </button>
        <button
          onClick={() => setShowFloorplan(true)}
          className="flex items-center justify-center gap-2 bg-gray-800 text-[#c1b428] border border-gray-700 rounded-xl py-3 text-xs font-semibold"
        >
          🏠 Prikaz stolova
        </button>
        <button
          onClick={() => setShowFloorplanPrint(true)}
          className="col-span-2 flex items-center justify-center gap-2 bg-gray-800 text-[#c1b428] border border-gray-700 rounded-xl py-3 text-xs font-semibold"
        >
          🖨️ Ispis rezervacija po stolovima
        </button>
      </div>

      {/* Lista rezervacija */}
      <div className="flex-1 overflow-y-auto px-4 pb-6 space-y-2">
        {loading ? (
          <p className="text-center text-gray-400 py-8">Učitavam...</p>
        ) : allSorted.length > 0 ? (
          allSorted.map((r) => {
            const past = isPast(r);
            return (
              <div
                key={r.id}
                className={`rounded-xl border p-3 transition-opacity ${
                  past ? "bg-gray-900 border-gray-700 opacity-40" : "bg-gray-900 border-gray-700"
                }`}
              >
                <div className="flex justify-between items-center mb-1">
                  <span className={`text-black font-bold px-2 py-0.5 rounded text-xs ${
                    past ? "bg-gray-500" : "bg-[#c1b428]"
                  }`}>
                    {r.tables.join(", ")}
                  </span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded text-white ${getPeriodColor(r.start_datetime)}`}>
                    {r.start_datetime} – {r.end_datetime}
                  </span>
                </div>
                {/* Donji red – ime, broj gostiju, akcije */}
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-sm">{r.guest_name}</span>

                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-400 text-xs">👥 {r.guests}</span>

                    {["moderator", "admin"].includes(user?.role) && (
                      <>
                        <button
                          onClick={() => setSelectedReservation(r)}
                          className="p-1 bg-gray-700 text-[#c1b428] rounded text-xs hover:bg-gray-600"
                          title="Uredi"
                        >
                          ✏️
                        </button>

                        <button
                          onClick={() => setConfirmDeleteMobile(r)}
                          className="p-1 bg-gray-700 text-red-400 rounded text-xs hover:bg-red-900"
                          title="Obriši"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {r.note && (
                  <div className="mt-1 text-xs text-gray-400 italic">
                    📝 {r.note}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 text-sm">Nema rezervacija za odabrani datum.</p>
          </div>
        )}
      </div>

      {/* Modali */}
      {showNewReservation && (
        <MobileReservationModal
          tables={tables}
          shifts={shifts}
          selectedDate={selectedDate}
          onClose={() => setShowNewReservation(false)}
          onSuccess={() => {
            setShowNewReservation(false);
            refreshData();
          }}
        />
      )}

      {showSearch && (
        <MobileSearchModal
          onClose={() => setShowSearch(false)}
          user={user}
        />
      )}

      {showFloorplan && (
        <FloorplanOverlay
          onClose={() => setShowFloorplan(false)}
          selectedDate={selectedDate}
          shifts={shifts}
          user={user}
        />
      )}

      {/* Edit modal */}
      {selectedReservation && (
        <MobileEditModal
          reservation={selectedReservation}
          tables={tables}
          onClose={() => setSelectedReservation(null)}
          onSuccess={() => {
            setSelectedReservation(null);
            refreshData();
          }}
        />
      )}

      {/* Confirm delete */}
      {confirmDeleteMobile && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-[#c1b428] rounded-xl p-6 w-80 text-center">
            <div className="text-3xl mb-3">🗑️</div>
            <h3 className="text-[#c1b428] font-bold text-sm mb-2">Brisanje rezervacije</h3>
            <p className="text-gray-300 text-xs mb-5">
              Obrisati rezervaciju za <span className="text-[#c1b428] font-bold">{confirmDeleteMobile.guest_name}</span>?
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setConfirmDeleteMobile(null)}
                className="px-4 py-2 bg-gray-700 text-[#c1b428] rounded text-xs font-semibold"
              >
                Odustani
              </button>
              <button
                onClick={async () => {
                  await fetch(`/reservations/api/reservations/${confirmDeleteMobile.id}/delete/`, { method: "DELETE" });
                  setConfirmDeleteMobile(null);
                  refreshData();
                }}
                className="px-4 py-2 bg-red-700 text-white rounded text-xs font-bold"
              >
                Obriši
              </button>
            </div>
          </div>
        </div>
      )}

      {showFloorplanPrint && (
        <FloorplanPrintModal
          date={selectedDate}
          onClose={() => setShowFloorplanPrint(false)}
          mobileMode={true}
        />
      )}

      {showSettings && (
        <SettingsModal
          onClose={() => setShowSettings(false)}
          currentFloor={localStorage.getItem('defaultFloor') || 'inside'}
          onFloorChange={(floor) => {
            localStorage.setItem('defaultFloor', floor);
          }}
        />
      )}
    </div>
  );
};

export default MobileDashboard;